import {useTranslation} from '@/translations/i18n'
import {
    TreatmentRequestExtended,
    TreatmentRequestScoresValidationSchema,
    DiseaseExtended
} from '@/features/treatment-requests/types'
import {StyledTreatmentRequestDetailsScoresSection} from './style'
import {useTreatmentRequestEditModeView} from '@/features/treatment-requests/hooks/useTreatmentRequestEditModeView'
import {FormProvider, useForm, useFormContext} from 'react-hook-form'
import {TreatmentRequestScoresForm} from '@/features/treatment-requests/components/treatment-request-scores-form/TreatmentRequestScoresForm'
import {TreatmentRequestScoresDetails} from '@/features/treatment-requests/components/treatment-request-scores-details/TreatmentRequestScoresDetails'
import {TreatmentRequestEditButtons} from '@/features/treatment-requests/components/treatment-request-edit-buttons/TreatmentRequestEditButtons'
import {getDirtyFieldsFromFormValues, ObjectKeys} from '@/utilities/helpers'
import {useOutletContext} from 'react-router-dom'
import {useUpdateTreatmentRequest} from '@/features/treatment-requests/services/useUpdateTreatmentRequest'
import {useTreatmentRequestFormSubmission} from '@/features/treatment-requests/hooks/useTreatmentRequestFormSubmission'
import {useTreatmentRequestDetailsStore} from '@/features/treatment-requests/store/treatmentRequestDetailsStore'
import {remapTreatmentRequestToDefaultValues, remapTreatmentRequestToPayload} from '@/features/treatment-requests/utils'
import {useTreatmentRequestScores} from '@/features/treatment-requests/hooks/useTreatmentRequestScores'
import {zodResolver} from '@hookform/resolvers/zod'
import {usePermissions} from '@/features/auth/store/store'
import {useEffect} from 'react'

export const TreatmentRequestScoresSection = () => {
    const {treatmentRequest} = useOutletContext<{treatmentRequest: TreatmentRequestExtended}>()

    const defaultValues = remapTreatmentRequestToDefaultValues(treatmentRequest)

    const {defaultScores} = useTreatmentRequestScores({
        serviceType: treatmentRequest.serviceType,
        existingScores: defaultValues.scores,
        diseases: defaultValues.diseases
    })

    const form = useForm<TreatmentRequestScoresValidationSchema>({
        mode: 'onBlur',
        resolver: zodResolver(TreatmentRequestScoresValidationSchema),
        defaultValues: {
            scores: defaultScores,
            diseases: defaultValues.diseases
        }
    })

    // Update form when diseases change to sync with diagnosis section
    useEffect(() => {
        const currentDiseases = form.getValues('diseases')
        const newDiseases = defaultValues.diseases

        // Check if diseases have changed
        const diseasesChanged = JSON.stringify(currentDiseases) !== JSON.stringify(newDiseases)

        if (diseasesChanged) {
            // Update diseases in form
            form.setValue('diseases', newDiseases, {shouldDirty: false})

            // Recalculate scores based on new diseases
            const canDisplaySubType = newDiseases?.some((disease: DiseaseExtended) => disease.name === 'I° Gruppo')
            const currentScores = form.getValues('scores')

            // Filter out subtype scores if diseases don't include "I° Gruppo"
            const filteredScores = currentScores.filter(score => {
                if (score.type === 'subtype' && !canDisplaySubType) {
                    return false
                }
                return true
            })

            // Update scores if they changed
            if (filteredScores.length !== currentScores.length) {
                form.setValue('scores', filteredScores, {shouldDirty: false})
            }
        }
    }, [defaultValues.diseases, form])

    return (
        <FormProvider {...form}>
            <TreatmentRequestScoresSectionContent treatmentRequest={treatmentRequest} />
        </FormProvider>
    )
}

const TreatmentRequestScoresSectionContent = ({treatmentRequest}: {treatmentRequest: TreatmentRequestExtended}) => {
    const {t} = useTranslation()
    const form = useFormContext<TreatmentRequestScoresValidationSchema>()
    const {onSuccessSubmit, onErrorSubmit, scrollToTop} = useTreatmentRequestFormSubmission({
        formSchema: TreatmentRequestScoresValidationSchema
    })
    const {isEditModeEnabled, isDisabledView} = useTreatmentRequestEditModeView('scores')
    const setIsErrorBannerVisible = useTreatmentRequestDetailsStore(state => state.setIsErrorBannerVisible)
    const permissions = usePermissions()
    // mutation
    const updateTreatmentRequest = useUpdateTreatmentRequest({
        urlParams: {treatmentRequestId: treatmentRequest.id},
        params: {responseType: 'extended'},
        options: {
            onSuccess: onSuccessSubmit,
            onError: onErrorSubmit
        }
    })

    // actions
    const handleSubmit = form.handleSubmit(
        formValues => {
            const dirtyFormValues = getDirtyFieldsFromFormValues(form.formState.dirtyFields, formValues, [])
            const parsedValues = remapTreatmentRequestToPayload(dirtyFormValues)
            return updateTreatmentRequest.mutate(parsedValues)
        },
        errors => {
            setIsErrorBannerVisible(true)
            console.error(errors)
            scrollToTop()
        }
    )

    return (
        <StyledTreatmentRequestDetailsScoresSection
            isDisabled={isDisabledView}
            title={
                treatmentRequest.serviceType == 'adults'
                    ? t('treatment_requests:score_adults')
                    : t('treatment_requests:score_evolutionary_age')
            }
            headingEndSlot={
                permissions?.requests?.write && (
                    <TreatmentRequestEditButtons
                        groupName="scores"
                        isConfirmDisabled={!ObjectKeys(form.formState.dirtyFields).length}
                        onCancel={form.reset}
                        onConfirm={handleSubmit}
                        isDisabled={treatmentRequest.status == 'accepted' || treatmentRequest.status == 'rejected'}
                    />
                )
            }
        >
            {isEditModeEnabled ? (
                <TreatmentRequestScoresForm serviceType={treatmentRequest.serviceType} />
            ) : (
                <TreatmentRequestScoresDetails treatmentRequest={treatmentRequest} />
            )}
        </StyledTreatmentRequestDetailsScoresSection>
    )
}
